import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  useTheme, Container, Box, Typography, Divider, Stack, Grid, Collapse, Fade,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import useUserFilesv2 from '../common/useUserFilesv2';
import DocumentTypeFilter from './components/DocumentTypeFilter';
import Search from '../common/Search';
import StyledSelect from '../common/StyledSelect';
import DocumentsTable from './components/DocumentsTable';
import MobileDocuments from './components/MobileDocuments';

const allValuesSelectedToken = 'ALL_VALUES';
const DOCUMENT_TYPE_LABELS = {
  [allValuesSelectedToken]: 'All Documents',
  Fund_Summary_Performance_Report: 'Fund Performance Reports',
  Exit_Memo: 'Investment Memos',
  K1_Tax_Form: 'Tax Documents',
  Personal_Portfolio_Report: 'Personal Portfolio Reports',
  User_Investment_Plan: 'Venture Plans',
};

// New mapping for singular display values
const DOCUMENT_TYPE_SINGULAR_LABELS = {
  [allValuesSelectedToken]: 'All Documents',
  Fund_Summary_Performance_Report: 'Fund Performance Report',
  Exit_Memo: 'Investment Memo',
  K1_Tax_Form: 'Tax Document',
  Personal_Portfolio_Report: 'Personal Portfolio Report',
  User_Investment_Plan: 'Venture Plan',
};

const InvestorStatements = () => {
  const theme = useTheme();
  const desktopSize = useMediaQuery(theme.breakpoints.up('md'));
  const currentManagedUser = useSelector((state) => state.currentManagedUser);
  const { userFiles, isLoading, isError } = useUserFilesv2(currentManagedUser);

  const [showFilters, setShowFilters] = useState(true);
  const [documentTypeFilter, setDocumentTypeFilter] = useState(allValuesSelectedToken);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [filteredSearchData, setFilteredSearchData] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([
    { label: DOCUMENT_TYPE_LABELS[allValuesSelectedToken], value: allValuesSelectedToken },
  ]);
  const [years, setYears] = useState([]);
  const [funds, setFunds] = useState([]);
  const [filteredFunds, setFilteredFunds] = useState([]);

  const computeApplicableYears = (docType) => {
    if (!userFiles || !Array.isArray(userFiles)) {
      return [{ label: 'All Years', value: allValuesSelectedToken }];
    }
    let files = userFiles;
    if (docType !== allValuesSelectedToken) {
      files = files.filter((file) => file.documentType === docType);
    }
    const yearSet = new Set();
    files.forEach((file) => {
      let yr;
      if (docType === 'K1_Tax_Form') {
        if (file.year) {
          yr = file.year;
        }
      } else if (file.publicationDate) {
        yr = new Date(file.publicationDate).getFullYear();
      } else if (file.year) {
        yr = file.year;
      }
      if (yr) {
        yearSet.add(yr);
      }
    });
    const sortedYears = Array.from(yearSet).sort((a, b) => b - a);
    const options = [{ label: 'All Years', value: allValuesSelectedToken }];
    if (docType !== 'K1_Tax_Form') {
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      const qualifiesLast12 = files.some(
        (file) => file.publicationDate && new Date(file.publicationDate) >= twelveMonthsAgo,
      );
      if (qualifiesLast12) {
        options.push({ label: 'Last 12 Months', value: 'last12' });
      }
    }
    sortedYears.forEach((year) => {
      options.push({ label: year.toString(), value: year });
    });
    return options;
  };

  const computeApplicableFunds = (docType, selectedYearsValues) => {
    if (!userFiles || !Array.isArray(userFiles)) {
      return [{ label: 'All Funds', value: allValuesSelectedToken }];
    }
    let files = userFiles;
    if (docType !== allValuesSelectedToken) {
      files = files.filter((file) => file.documentType === docType);
    }
    if (!selectedYearsValues.includes(allValuesSelectedToken)) {
      files = files.filter((file) => {
        let qualifies = false;
        if (selectedYearsValues.includes('last12')) {
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          if (file.publicationDate && new Date(file.publicationDate) >= twelveMonthsAgo) {
            qualifies = true;
          }
        }
        const fileYear = file.publicationDate
          ? new Date(file.publicationDate).getFullYear()
          : file.year;
        if (selectedYearsValues.includes(fileYear)) {
          qualifies = true;
        }
        return qualifies;
      });
    }
    const fundsMap = new Map();
    files.forEach((file) => {
      if (file.fundName) {
        const key = file.fundName.toLowerCase();
        if (!fundsMap.has(key)) {
          fundsMap.set(key, file.fundName);
        }
      }
      if (file.vintageFundNames && Array.isArray(file.vintageFundNames)) {
        file.vintageFundNames.forEach((name) => {
          const key = name.toLowerCase();
          if (!fundsMap.has(key)) {
            fundsMap.set(key, name);
          }
        });
      }
    });
    const fundsArr = Array.from(fundsMap.values()).sort((a, b) => new Intl.Collator('en', { numeric: true, sensitivity: 'base' }).compare(a, b));
    const options = [{ label: 'All Funds', value: allValuesSelectedToken }];
    fundsArr.forEach((fund) => options.push({ label: fund, value: fund }));
    return options;
  };

  const filterFiles = (yearFilter, dataToFilter, docType, fundsFilter) => dataToFilter.filter((file) => {
    const matchesDocType = docType === allValuesSelectedToken || file.documentType === docType;
    let matchesYear = false;
    if (yearFilter.includes(allValuesSelectedToken)) {
      matchesYear = true;
    } else {
      if (docType !== 'K1_Tax_Form' && yearFilter.includes('last12')) {
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        if (file.publicationDate && new Date(file.publicationDate) >= twelveMonthsAgo) {
          matchesYear = true;
        }
      }
      const fileYear = docType === 'K1_Tax_Form'
        ? file.year
        : file.publicationDate
          ? new Date(file.publicationDate).getFullYear()
          : file.year;
      if (yearFilter.includes(fileYear)) {
        matchesYear = true;
      }
    }
    const matchesFund = fundsFilter.includes(allValuesSelectedToken)
        || fundsFilter.some(
          (fund) => file.fundName === fund
            || (file.vintageFundNames
              && Array.isArray(file.vintageFundNames)
              && file.vintageFundNames.some(
                (code) => code.toUpperCase() === fund.toUpperCase(),
              )),
        );
    return matchesDocType && matchesYear && matchesFund;
  });

  const filterFilesAndDisplay = (
    dataToFilter,
    docType,
    yearsFilter,
    fundsFilter,
    changeYearFilterIfEmpty = false,
  ) => {
    let filteredFiles = filterFiles(yearsFilter, dataToFilter, docType, fundsFilter);
    if (changeYearFilterIfEmpty && filteredFiles.length === 0) {
      setSelectedYears([allValuesSelectedToken]);
      filteredFiles = filterFiles(
        [allValuesSelectedToken],
        dataToFilter,
        docType,
        fundsFilter,
      );
    }
    // Enrich each file record with a singular documentTypeDisplayName
    const enrichedFiles = filteredFiles.map((file) => ({
      ...file,
      documentTypeDisplayName: DOCUMENT_TYPE_SINGULAR_LABELS[file.documentType] || '',
    }));
    setDisplayedData(enrichedFiles);
  };

  useEffect(() => {
    if (userFiles && Array.isArray(userFiles) && userFiles.length > 0) {
      const docTypes = [
        { label: DOCUMENT_TYPE_LABELS[allValuesSelectedToken], value: allValuesSelectedToken },
        ...Array.from(new Set(userFiles.map((file) => file.documentType)))
          .filter((dt) => DOCUMENT_TYPE_LABELS[dt])
          .map((dt) => ({ label: DOCUMENT_TYPE_LABELS[dt], value: dt }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
      setDocumentTypes(docTypes);
      const yearsOptions = computeApplicableYears(documentTypeFilter);
      setYears(yearsOptions);
      if (documentTypeFilter === 'K1_Tax_Form') {
        const numericYears = yearsOptions.filter((o) => typeof o.value === 'number');
        const currentYear = new Date().getFullYear();
        const currentTaxYear = numericYears.find((o) => o.value === currentYear)
          || (numericYears.length > 0 ? numericYears[0] : null);
        setSelectedYears(currentTaxYear ? [currentTaxYear.value] : [allValuesSelectedToken]);
      } else if (documentTypeFilter === allValuesSelectedToken) {
        const hasLast12 = yearsOptions.some((o) => o.value === 'last12');
        setSelectedYears(hasLast12 ? ['last12'] : [allValuesSelectedToken]);
      } else {
        const individualOptions = yearsOptions
          .filter((o) => o.value !== allValuesSelectedToken)
          .map((o) => o.value);
        setSelectedYears([...individualOptions, allValuesSelectedToken]);
      }
      const fundsOptions = computeApplicableFunds(documentTypeFilter, [allValuesSelectedToken]);
      setFunds(fundsOptions);
      setFilteredFunds(fundsOptions);
      const individualFundOptions = fundsOptions
        .filter((o) => o.value !== allValuesSelectedToken)
        .map((o) => o.value);
      setSelectedFunds([...individualFundOptions, allValuesSelectedToken]);
      setFilteredSearchData(userFiles);
      filterFilesAndDisplay(
        userFiles,
        documentTypeFilter,
        documentTypeFilter === 'K1_Tax_Form'
          ? yearsOptions.filter((o) => typeof o.value === 'number').length > 0
            ? [yearsOptions.filter((o) => typeof o.value === 'number')[0].value]
            : [allValuesSelectedToken]
          : documentTypeFilter === allValuesSelectedToken
            ? yearsOptions.some((option) => option.value === 'last12')
              ? ['last12']
              : yearsOptions
                .filter((o) => o.value !== allValuesSelectedToken)
                .map((o) => o.value)
                .concat(allValuesSelectedToken)
            : [allValuesSelectedToken],
        individualFundOptions.concat(allValuesSelectedToken),
        true,
      );
    }
  }, [userFiles]);

  useEffect(() => {
    if (userFiles && Array.isArray(userFiles) && userFiles.length > 0) {
      const newYearsOptions = computeApplicableYears(documentTypeFilter);
      setYears(newYearsOptions);
      if (documentTypeFilter === 'K1_Tax_Form') {
        const numericYears = newYearsOptions.filter((o) => typeof o.value === 'number');
        const currentYear = new Date().getFullYear();
        const currentTaxYear = numericYears.find((o) => o.value === currentYear)
          || (numericYears.length > 0 ? numericYears[0] : null);
        setSelectedYears(currentTaxYear ? [currentTaxYear.value] : [allValuesSelectedToken]);
      } else if (documentTypeFilter === allValuesSelectedToken) {
        const hasLast12 = newYearsOptions.some((o) => o.value === 'last12');
        setSelectedYears(hasLast12 ? ['last12'] : [allValuesSelectedToken]);
      } else {
        const individualOptions = newYearsOptions
          .filter((o) => o.value !== allValuesSelectedToken)
          .map((o) => o.value);
        setSelectedYears([...individualOptions, allValuesSelectedToken]);
      }
      const newFundsOptions = computeApplicableFunds(documentTypeFilter, [allValuesSelectedToken]);
      setFunds(newFundsOptions);
      setFilteredFunds(newFundsOptions);
      const individualFundOptions = newFundsOptions
        .filter((o) => o.value !== allValuesSelectedToken)
        .map((o) => o.value);
      setSelectedFunds([...individualFundOptions, allValuesSelectedToken]);
      filterFilesAndDisplay(
        filteredSearchData,
        documentTypeFilter,
        documentTypeFilter === 'K1_Tax_Form'
          ? newYearsOptions.filter((o) => typeof o.value === 'number').length > 0
            ? [newYearsOptions.filter((o) => typeof o.value === 'number')[0].value]
            : [allValuesSelectedToken]
          : documentTypeFilter === allValuesSelectedToken
            ? newYearsOptions.some((option) => option.value === 'last12')
              ? ['last12']
              : newYearsOptions
                .filter((o) => o.value !== allValuesSelectedToken)
                .map((o) => o.value)
                .concat(allValuesSelectedToken)
            : [allValuesSelectedToken],
        individualFundOptions.concat(allValuesSelectedToken),
      );
    }
  }, [documentTypeFilter, userFiles]);

  useEffect(() => {
    if (userFiles && Array.isArray(userFiles) && userFiles.length > 0) {
      const newFundsOptions = computeApplicableFunds(documentTypeFilter, selectedYears);
      setFunds(newFundsOptions);
      setFilteredFunds(newFundsOptions);
      filterFilesAndDisplay(filteredSearchData, documentTypeFilter, selectedYears, selectedFunds);
    }
  }, [selectedYears, documentTypeFilter, userFiles]);

  const handleDocumentTypeChange = (selectedValue) => {
    setDocumentTypeFilter(selectedValue);
    filterFilesAndDisplay(filteredSearchData, selectedValue, selectedYears, selectedFunds);
  };

  const handleYearChange = (selectedValues) => {
    setSelectedYears((prev) => {
      const prevHasAll = prev.includes(allValuesSelectedToken);
      const newHasAll = selectedValues.includes(allValuesSelectedToken);
      let finalSelection = [];
      if (!prevHasAll && newHasAll) {
        finalSelection = [allValuesSelectedToken];
      } else if (prevHasAll && newHasAll) {
        finalSelection = selectedValues.filter((val) => val !== allValuesSelectedToken);
      } else {
        finalSelection = selectedValues;
      }
      filterFilesAndDisplay(filteredSearchData, documentTypeFilter, finalSelection, selectedFunds);
      return finalSelection;
    });
  };

  const handleFundChange = (selectedValues) => {
    setSelectedFunds((prev) => {
      const prevHasAll = prev.includes(allValuesSelectedToken);
      const newHasAll = selectedValues.includes(allValuesSelectedToken);
      let finalSelection = [];
      if (!prevHasAll && newHasAll) {
        finalSelection = [allValuesSelectedToken];
      } else if (prevHasAll && newHasAll) {
        finalSelection = selectedValues.filter((val) => val !== allValuesSelectedToken);
      } else {
        finalSelection = selectedValues;
      }
      filterFilesAndDisplay(filteredSearchData, documentTypeFilter, selectedYears, finalSelection);
      return finalSelection;
    });
  };

  const onSearchClear = () => {
    filterFilesAndDisplay(userFiles, documentTypeFilter, selectedYears, selectedFunds);
    setFilteredSearchData(userFiles);
  };

  const onSearch = (filtered) => {
    setFilteredSearchData(filtered);
    filterFilesAndDisplay(filtered, documentTypeFilter, selectedYears, selectedFunds);
  };

  return (
    <>
      <Helmet>
        <title>Documents & Statements - Alumni Ventures</title>
        <link
          rel="shortcut icon"
          href={`${process.env.AVG_API_PATH}/favicon.ico`}
          sizes="16x16"
        />
      </Helmet>
      <Container maxWidth="xl" sx={{ backgroundColor: 'white' }}>
        <Box pt={4}>
          <Typography variant="h1" sx={{ mb: { xs: 3, sm: 0 } }}>
            Documents & Statements
          </Typography>
          <Divider sx={{ my: 3.5, display: { xs: 'none', sm: 'block' } }} />
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              flexDirection: 'column',
              mb: showFilters ? 2.5 : 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                color: 'secondary.main',
                mb: 2,
              }}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, color: 'secondary.main' }}>
                {showFilters ? 'Hide filters' : 'Show filters'}
              </Typography>
              {showFilters ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </Box>
            <Divider />
          </Box>
          <Grid
            container
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ flexWrap: 'nowrap' }}
          >
            <Grid item xs={12} sm="auto">
              <Collapse in={showFilters || desktopSize} sx={{ display: { sm: 'block' } }}>
                <Box sx={{ pt: { xs: 0, sm: 8 } }}>
                  <DocumentTypeFilter
                    options={documentTypes}
                    value={documentTypeFilter}
                    onChange={handleDocumentTypeChange}
                  />
                </Box>
              </Collapse>
            </Grid>
            <Grid
              item
              xs={12}
              sm
              sx={{ '&.MuiGrid-item': { paddingTop: { xs: 2, sm: 3 } } }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{
                  mb: 3,
                  '& > :last-child': { mt: showFilters ? { xs: 2, sm: 0 } : 0 },
                }}
              >
                <Collapse
                  in={showFilters || desktopSize}
                  sx={{
                    display: { sm: 'block' },
                    flexGrow: 1,
                    maxWidth: { xs: 'none', sm: 376 },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={0}
                    flexWrap="wrap"
                    sx={{ rowGap: { xs: 2, sm: 1 }, columnGap: 2 }}
                  >
                    <StyledSelect
                      options={years}
                      label={documentTypeFilter === 'K1_Tax_Form' ? 'Tax Year' : 'Year'}
                      value={selectedYears}
                      onChange={handleYearChange}
                      multiple
                    />
                    <StyledSelect
                      options={filteredFunds.length > 0 ? filteredFunds : funds}
                      label="Fund"
                      value={selectedFunds}
                      onChange={handleFundChange}
                      multiple
                    />
                  </Stack>
                </Collapse>
                <Search
                  label="Search..."
                  data={userFiles}
                  onClear={onSearchClear}
                  onSearch={onSearch}
                  disabled={!userFiles || !userFiles.length}
                  fieldsToSearchOn={['title', 'vintageFundNames', 'fundName']}
                  clearOnBlur={false}
                  textFieldSx={{ width: { xs: '100%', sm: '220px' } }}
                />
              </Stack>

              {desktopSize && !isLoading && (
                <Fade
                  key={`fade-desktop-${documentTypeFilter}`}
                  in={!isLoading && !isError}
                  timeout={150}
                >
                  <Box style={{ marginBottom: '100px' }}>
                    <DocumentsTable
                      documentsData={displayedData}
                      currentManagedUserId={currentManagedUser || null}
                      isLoading={isLoading}
                      isError={isError}
                      hideSelectionColumn={documentTypeFilter !== 'K1_Tax_Form'}
                      alternateTitleLabel={
                        documentTypeFilter === 'K1_Tax_Form'
                          ? 'K-1 Prepared for Investor'
                          : null
                      }
                      selectAllRowsOnLoad={documentTypeFilter === 'K1_Tax_Form'}
                      hideDocumentTypeColumn={documentTypeFilter !== allValuesSelectedToken}
                    />
                  </Box>
                </Fade>
              )}
              {!desktopSize && !isLoading && (
                <Fade
                  key={`fade-mobile-${documentTypeFilter}`}
                  in={!isLoading && !isError}
                  timeout={150}
                >
                  <Box style={{ marginBottom: '100px' }}>
                    <MobileDocuments
                      documentsData={displayedData}
                      currentManagedUserId={currentManagedUser || null}
                      isLoading={isLoading}
                      isError={isError}
                    />
                  </Box>
                </Fade>
              )}
              {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                  <Loader />
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default InvestorStatements;
